// The frozen 15. This list is the single source of truth for the UI, and it
// mirrors the `categories` table, which is locked by trigger in the database.
//
// Changing an id breaks comparability with the 3,520 events already collected.
// Adding a capability must be additive.
//
// `countsTables` marks the three where a re-tap increments a table count
// WITHOUT ending the activity block. Verified against the pilot: bussing-empty
// is 105 taps over 838 seconds at 8.0 sec/instance, and 838/105 = 7.98, which
// only reconciles if the opening tap counts as one.
//
// Order and names are the original tool's, at the pilot owners' request of
// 2026-09-22: the tiles follow the database's ordinal and show its full label.
// Layout 1 had moved Wiping up beside the two bussing tiles and shown short
// names, which observers trained on the original had to relearn. categories.test.ts
// holds this list to the database seed, so the two cannot drift apart.
//
// `grid` is the tile position. Observers build muscle memory, so moving a tile
// changes the data as surely as renaming it would. Nothing stores the layout
// with each session: data taken on layout 1 is told from layout 2 by the date
// this change reached the field.

export type CategoryId =
  | "bussing_empty" | "bussing_occupied" | "waste_sorting" | "dish_tray"
  | "wiping" | "moving_furniture" | "bin_management" | "walking_empty"
  | "walking_carrying" | "floor_cleaning" | "equipment" | "restocking"
  | "idle" | "lost_sight" | "other";

export interface Category {
  id: CategoryId;
  label: string;
  countsTables: boolean;
  requiresNote: boolean;
  grid: number;
}

export const CATEGORIES: readonly Category[] = [
  { id: "bussing_empty",    label: "Bussing table - empty",              countsTables: true,  requiresNote: false, grid: 1 },
  { id: "bussing_occupied", label: "Bussing table - occupied",           countsTables: true,  requiresNote: false, grid: 2 },
  { id: "waste_sorting",    label: "Waste sorting / disposal",           countsTables: false, requiresNote: false, grid: 3 },
  { id: "dish_tray",        label: "Dish / tray management",             countsTables: false, requiresNote: false, grid: 4 },
  { id: "wiping",           label: "Wiping tables / chairs",             countsTables: true,  requiresNote: false, grid: 5 },
  { id: "moving_furniture", label: "Moving furniture",                   countsTables: false, requiresNote: false, grid: 6 },
  { id: "bin_management",   label: "Bin management",                     countsTables: false, requiresNote: false, grid: 7 },
  { id: "walking_empty",    label: "Walking - without collected items",  countsTables: false, requiresNote: false, grid: 8 },
  { id: "walking_carrying", label: "Walking - carrying collected items", countsTables: false, requiresNote: false, grid: 9 },
  { id: "floor_cleaning",   label: "Floor cleaning",                     countsTables: false, requiresNote: false, grid: 10 },
  { id: "equipment",        label: "Cleaning / servicing equipment",     countsTables: false, requiresNote: false, grid: 11 },
  { id: "restocking",       label: "Re-stocking supplies",               countsTables: false, requiresNote: false, grid: 12 },
  { id: "idle",             label: "Idle or waiting",                    countsTables: false, requiresNote: false, grid: 13 },
  { id: "lost_sight",       label: "Lost sight / unclear",               countsTables: false, requiresNote: false, grid: 14 },
  // The guide instructs Other is never recorded without a comment, because the
  // post-survey pass reads those comments looking for a recurring category.
  { id: "other",            label: "Other",                              countsTables: false, requiresNote: true,  grid: 15 },
] as const;

export const BY_ID = new Map(CATEGORIES.map((c) => [c.id, c]));
export const LAYOUT_VERSION = 2;
